import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import { rentalService } from '../../services/rental.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import { ArrowLeftIcon, UploadIcon, XIcon, HomeIcon } from '../../components/icons/Icons';

/* ─── tiny style tokens ─────────────────────────────────── */
const indigo = {
  50:  '#eef2ff',
  100: '#e0e7ff',
  200: '#c7d2fe',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
};

const card = {
  background: '#fff',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 4px 20px rgba(99,102,241,.07)',
  border: '1px solid #e8eaf6',
  padding: '32px',
  marginBottom: '24px',
};

const sectionTitle = {
  fontSize: '15px',
  fontWeight: '700',
  color: '#1e1b4b',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const label = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '6px',
};

const inputBase = {
  width: '100%',
  padding: '11px 14px',
  fontSize: '14px',
  border: '1.5px solid #e5e7eb',
  borderRadius: '10px',
  outline: 'none',
  fontFamily: 'inherit',
  transition: 'border-color .2s, box-shadow .2s',
  background: '#fafafa',
  boxSizing: 'border-box',
};

/* ─── Drag-and-drop photo upload zone ───────────────────── */
const PhotoUpload = ({ images, onAdd, onRemove }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (images.length + imgs.length > 8) {
      alert('Maximum 8 images allowed');
      return;
    }
    onAdd(imgs);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [images]);

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? indigo[500] : '#c7d2fe'}`,
          borderRadius: '14px',
          background: dragging ? indigo[50] : '#f8f9ff',
          padding: '40px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all .2s',
          marginBottom: '16px',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div style={{ marginBottom: '10px' }}>
          <UploadIcon size={36} color={indigo[400]} />
        </div>
        <p style={{ fontWeight: '600', color: indigo[700], margin: 0, fontSize: '14px' }}>
          {dragging ? 'Drop images here…' : 'Drag & drop photos, or click to browse'}
        </p>
        <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
          JPEG, PNG, WEBP — up to 8 photos, 5 MB each
        </p>
      </div>

      {/* Thumbnails */}
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px' }}>
          {images.map((file, idx) => (
            <div
              key={idx}
              style={{
                position: 'relative',
                borderRadius: '10px',
                overflow: 'hidden',
                aspectRatio: '1 / 1',
                background: '#f3f4f6',
                boxShadow: '0 2px 8px rgba(0,0,0,.08)',
              }}
            >
              <img
                src={URL.createObjectURL(file)}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={() => onRemove(idx)}
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  background: 'rgba(0,0,0,.55)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <XIcon size={12} color="#fff" />
              </button>
              {idx === 0 && (
                <span style={{
                  position: 'absolute',
                  bottom: '5px',
                  left: '5px',
                  background: indigo[600],
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: '700',
                  borderRadius: '4px',
                  padding: '1px 6px',
                }}>
                  Cover
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Floating label input helper ───────────────────────── */
const Field = ({ label: lbl, error, children, required }) => (
  <div style={{ marginBottom: '18px' }}>
    <label style={{ ...label }}>
      {lbl} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
    {children}
    {error && (
      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{error}</p>
    )}
  </div>
);

/* ─── Main page component ────────────────────────────────── */
const RentalCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({
    title: '',
    description: '',
    vacancies: '',
    rent_per_month: '',
    whatsapp_number: '',
  });
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.vacancies || parseInt(form.vacancies) < 1)
      errs.vacancies = 'At least 1 vacancy required';
    if (!form.rent_per_month || parseFloat(form.rent_per_month) < 0)
      errs.rent_per_month = 'Valid rent amount is required';
    if (!form.whatsapp_number || !form.whatsapp_number.trim())
      errs.whatsapp_number = 'WhatsApp number is required';
    else if (!/^[0-9]{10,15}$/.test(form.whatsapp_number))
      errs.whatsapp_number = 'Enter valid 10-15 digit number';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please fix the highlighted fields');
      return;
    }
    if (images.length === 0) {
      toast.error('Please upload at least one property photo');
      return;
    }

    setLoading(true);
    try {
      await rentalService.create(form, images);
      toast.success('Property listed successfully! 🎉');
      navigate('/seller/rentals');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to list property');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    ...inputBase,
    borderColor: errors[name] ? '#ef4444' : focusedField === name ? indigo[500] : '#e5e7eb',
    boxShadow: focusedField === name ? `0 0 0 3px ${indigo[100]}` : 'none',
  });

  return (
    <MainLayout>
      <div style={{ padding: '32px 24px', maxWidth: '760px', margin: '0 auto' }}>

        {/* ── Header ── */}
        <button
          onClick={() => navigate('/seller/rentals')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: indigo[600], fontSize: '14px', fontWeight: '600',
            marginBottom: '24px', padding: 0,
          }}
        >
          <ArrowLeftIcon size={16} />
          Back to My Rentals
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '32px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px',
            background: `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 14px ${indigo[200]}`,
          }}>
            <HomeIcon size={26} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e1b4b' }}>
              List Rental Property
            </h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
              Fill in the details below to publish your rental listing
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Photos ── */}
          <div style={card}>
            <h2 style={sectionTitle}>
              <span style={{ fontSize: '18px' }}>📸</span> Property Photos
            </h2>
            <PhotoUpload
              images={images}
              onAdd={(imgs) => setImages((prev) => [...prev, ...imgs])}
              onRemove={(idx) => setImages((prev) => prev.filter((_, i) => i !== idx))}
            />
          </div>

          {/* ── Basic Details ── */}
          <div style={card}>
            <h2 style={sectionTitle}>
              <span style={{ fontSize: '18px' }}>📋</span> Property Details
            </h2>

            <Field label="Property Title" required error={errors.title}>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                onFocus={() => setFocusedField('title')}
                onBlur={() => setFocusedField(null)}
                placeholder="e.g. Spacious 2 BHK Flat near Metro Station"
                style={inputStyle('title')}
              />
            </Field>

            <Field label="Description" error={errors.description}>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                onFocus={() => setFocusedField('description')}
                onBlur={() => setFocusedField(null)}
                placeholder="Describe the property — amenities, location, furnishing, rules, etc."
                rows={5}
                style={{
                  ...inputStyle('description'),
                  resize: 'vertical',
                  lineHeight: '1.6',
                }}
              />
            </Field>

            {/* Two-column row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Field label="Number of Vacancies" required error={errors.vacancies}>
                <input
                  type="number"
                  name="vacancies"
                  min="1"
                  value={form.vacancies}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('vacancies')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="e.g. 3"
                  style={inputStyle('vacancies')}
                />
              </Field>

              <Field label="Rent per Month" required error={errors.rent_per_month}>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '13px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: focusedField === 'rent_per_month' ? indigo[600] : '#6b7280',
                    pointerEvents: 'none',
                  }}>₹</span>
                  <input
                    type="number"
                    name="rent_per_month"
                    min="0"
                    step="any"
                    value={form.rent_per_month}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('rent_per_month')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="e.g. 12000"
                    style={{
                      ...inputStyle('rent_per_month'),
                      paddingLeft: '30px',
                    }}
                  />
                </div>
              </Field>
            </div>

            <Field label="WhatsApp Contact Number" required error={errors.whatsapp_number}>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '13px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: focusedField === 'whatsapp_number' ? indigo[600] : '#6b7280',
                  pointerEvents: 'none',
                }}>📱</span>
                <input
                  type="tel"
                  name="whatsapp_number"
                  value={form.whatsapp_number}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('whatsapp_number')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="9876543210"
                  maxLength="15"
                  style={{
                    ...inputStyle('whatsapp_number'),
                    paddingLeft: '38px',
                  }}
                />
              </div>
              <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px', marginBottom: 0 }}>
                Enter 10-15 digits without country code. Customers will contact you via WhatsApp.
              </p>
            </Field>
          </div>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => navigate('/seller/rentals')}
              disabled={loading}
              style={{
                padding: '12px 28px',
                border: `1.5px solid #e5e7eb`,
                borderRadius: '10px',
                background: '#fff',
                color: '#374151',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 36px',
                border: 'none',
                borderRadius: '10px',
                background: loading
                  ? indigo[400]
                  : `linear-gradient(135deg, ${indigo[500]}, ${indigo[700]})`,
                color: '#fff',
                fontWeight: '700',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: loading ? 'none' : `0 4px 14px ${indigo[200]}`,
                transition: 'all .2s',
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: '16px', height: '16px', border: '2px solid rgba(255,255,255,.4)',
                    borderTopColor: '#fff', borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  Listing…
                </>
              ) : (
                <>🏠 List Property</>
              )}
            </button>
          </div>
        </form>

        {/* Spinner keyframe */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </MainLayout>
  );
};

export default RentalCreate;
